import React, { useMemo, useRef, useState, type Ref } from 'react';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import ShareIconNew from '@atlaskit/icon/core/share';
import Popup from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ErrorFlagRenderer } from '@atlassian/jira-error-boundary-flag-renderer/src/ErrorBoundaryFlag.tsx';
import { messages } from './messages.tsx';
import { shareButtonEntryPoint } from './entrypoint.tsx';
import type { Props, RenderCustomTriggerButton } from './common/types.tsx';
import { EntrypointPropsProvider } from './common/utils/entrypoint-props-context.tsx';

type ShareButtonAsyncProps = Props & {
	errorBoundaryId: string;
	teamName: string;
};

const styles = cssMap({
	spinnerContainer: {
		textAlign: 'center',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

const LoadingIndicator = () => (
	<Box xcss={styles.spinnerContainer} role="menuitem">
		<Spinner size="small" />
	</Box>
);

const TriggerRenderer = ({
	isDisabled = false,
	isLoading = false,
	triggerRef,
	renderCustomTriggerButton,
	onClick = noop,
}: {
	isDisabled?: boolean;
	isLoading?: boolean;
	triggerRef?: React.Ref<HTMLElement>;
	renderCustomTriggerButton?: RenderCustomTriggerButton;
	onClick?: () => void;
}) => {
	const { formatMessage } = useIntl();
	const fakeRef = useRef<HTMLElement>(null);

	return renderCustomTriggerButton ? (
		renderCustomTriggerButton(
			{ isDisabled, isSelected: isLoading, onClick },
			{ ref: triggerRef ?? fakeRef, 'aria-expanded': false, 'aria-haspopup': false },
		)
	) : (
		<IconButton
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			ref={(triggerRef ?? fakeRef) as Ref<HTMLButtonElement>}
			testId="share-button.button"
			label={formatMessage(messages.shareTriggerButtonText)}
			appearance="default"
			isDisabled={isDisabled}
			isLoading={isLoading}
			icon={() => <ShareIconNew spacing="spacious" label="" color={token('color.icon')} />}
			onClick={onClick}
		/>
	);
};

export const ShareButtonAsync = ({
	errorBoundaryId,
	teamName,
	...props
}: ShareButtonAsyncProps): React.JSX.Element => {
	const { renderCustomTriggerButton } = props;
	const { Panel, trigger } = usePanel();
	const entryPointParams = useMemo(() => ({}), []);
	const [isOpen, setIsOpen] = useState(false);

	return (
		<JSErrorBoundary
			id={errorBoundaryId}
			packageName="jiraShareButton"
			teamName={teamName}
			fallback={({ error }) => (
				<>
					<TriggerRenderer renderCustomTriggerButton={renderCustomTriggerButton} isDisabled />
					<ErrorFlagRenderer id={errorBoundaryId} error={error} />
				</>
			)}
		>
			<EntrypointPropsProvider {...props}>
				<Trigger
					trigger={trigger}
					entryPoint={shareButtonEntryPoint}
					entryPointParams={entryPointParams}
					errorFallback={({ error }) => {
						// Throw error back up the stack to be caught and logged by the JSErrorBoundary
						throw error;
					}}
					fallback={
						<Popup
							content={LoadingIndicator}
							isOpen
							placement={props.dialogPlacement ?? 'left-start'}
							trigger={({ ref }) => (
								<TriggerRenderer
									renderCustomTriggerButton={renderCustomTriggerButton}
									isLoading
									triggerRef={ref}
								/>
							)}
						/>
					}
				>
					{({ ref }) =>
						!isOpen && (
							<TriggerRenderer
								triggerRef={ref}
								renderCustomTriggerButton={renderCustomTriggerButton}
								onClick={() => setIsOpen(true)}
							/>
						)
					}
				</Trigger>
				<Panel />
			</EntrypointPropsProvider>
		</JSErrorBoundary>
	);
};

export default ShareButtonAsync;
